import { useQuery } from '@apollo/client';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router';
import { ApolloProviderV4 } from '~/apollo/client-v4';
import { graphql } from '~/apollo/generated/v4';
import type { UploadRegionListPageQuery } from '~/apollo/generated/v4/graphql';
import { FilterSearch } from '~/components/common/FilterSearch';
import { NoItemsRow } from '~/components/common/NoItemsRow';
import { Panel } from '~/components/common/Panel';
import { SortTrigger } from '~/components/common/SortTrigger';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import { useSortFilter } from '~/hooks/data';
import {
  uploadRegionCreateRoute,
  uploadRegionUpdateOutcropsRoute,
  uploadRegionUpdateRoute,
} from '~/paths';

const REGION_LIST_PAGE = graphql(`
  query UploadRegionListPage {
    regionList {
      results {
        id
        name
        location
        country
        outcrops {
          id
        }
      }
    }
  }
`);

export default function UploadRegionListPage() {
  return (
    <ApolloProviderV4>
      <UploadRegionListPageInner />
    </ApolloProviderV4>
  );
}

type Region = NonNullable<
  NonNullable<UploadRegionListPageQuery['regionList']>['results']
>[number];
type SortableRegion = Region & {
  outcropCount: number;
};

function UploadRegionListPageInner() {
  const { data, loading } = useQuery(REGION_LIST_PAGE);

  const regions =
    data?.regionList?.results?.map<SortableRegion>(region => ({
      ...region,
      outcropCount: region.outcrops.length,
    })) ?? [];

  const {
    items,
    sortIndicatorProps: siProps,
    filterSearchProps,
  } = useSortFilter(regions, 'name', 'name', 'uploadRegionList');

  if (loading) return <SpinnerPlaceholder />;

  return (
    <Panel>
      <Panel.Heading className="flex justify-between">
        <Panel.Title>
          Regions
          <small className="text-muted ml-2">
            (<strong>{items.length}</strong> results displayed)
          </small>
        </Panel.Title>

        <Link
          to={uploadRegionCreateRoute()}
          className="btn btn-primary btn-xs gap-1"
        >
          <FontAwesomeIcon icon={faPlus} /> Create Region
        </Link>
      </Panel.Heading>
      <Panel.Body className="space-y-4">
        <FilterSearch {...filterSearchProps} />

        <table className="table w-full table-pin-rows table-compact">
          <thead>
            <tr>
              <th>
                <SortTrigger colName="id" sortIndicatorProps={siProps}>
                  ID
                </SortTrigger>
              </th>
              <th>
                <SortTrigger colName="name" sortIndicatorProps={siProps}>
                  Name
                </SortTrigger>
              </th>
              <th>
                <SortTrigger
                  colName="location.location"
                  sortIndicatorProps={siProps}
                  filterable
                >
                  Location
                </SortTrigger>
              </th>
              <th>
                <SortTrigger
                  colName="location.country"
                  sortIndicatorProps={siProps}
                  filterable
                >
                  Country
                </SortTrigger>
              </th>
              <th className="text-center">
                <SortTrigger
                  colName="outcropCount"
                  sortIndicatorProps={siProps}
                >
                  Outcrops
                </SortTrigger>
              </th>
            </tr>
          </thead>

          <tbody>
            <NoItemsRow show={!items.length} colSpan={6} />

            {items.map(region => (
              <tr key={region.id}>
                <td>{region.id}</td>
                <td>
                  <Link
                    to={uploadRegionUpdateRoute(parseInt(region.id))}
                    className="link"
                  >
                    {region.name}
                  </Link>
                </td>
                <td>{region.location}</td>
                <td>{region.country}</td>
                <td className="text-center">
                  <Link
                    to={uploadRegionUpdateOutcropsRoute(parseInt(region.id))}
                    className="link"
                  >
                    {region.outcropCount}
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel.Body>
    </Panel>
  );
}
